/**
 * Desktop mode database initialization
 *
 * This module handles async PGlite initialization for desktop mode.
 * Import this instead of @/db when running in desktop mode.
 *
 * Usage in desktop mode:
 *   import { db } from '@/db/desktop';
 */

import type { Kysely } from 'kysely';
import type { Sql } from 'postgres';

import { isDesktopBuild } from '@/lib/runtime';
import { createPGliteKysely } from './pglite-adapter';
import type { DB as DesktopSchema } from './schema';

// PGlite instance (initialized asynchronously)
let desktopDatabase: Kysely<DesktopSchema> | null = null;
let initializationPromise: Promise<Kysely<DesktopSchema>> | null = null;

/**
 * Get or create PGlite database instance
 * Initializes on first call, returns cached instance thereafter
 */
export function getDesktopDb(): Promise<Kysely<DesktopSchema>> {
  if (desktopDatabase !== null) {
    return Promise.resolve(desktopDatabase);
  }

  if (initializationPromise !== null) {
    return initializationPromise;
  }

  const pendingInitialization = (async () => {
    const instance: Kysely<DesktopSchema> = await createPGliteKysely();
    desktopDatabase = instance;
    initializationPromise = null;
    return instance;
  })();

  initializationPromise = pendingInitialization;
  return pendingInitialization;
}

/**
 * Synchronous access (throws if not initialized)
 * Only use this after ensuring database is initialized
 */
export function getDesktopDbSync(): Kysely<DesktopSchema> {
  if (desktopDatabase === null) {
    throw new Error(
      'Desktop database not initialized. Call getDesktopDb() first or use initializeDesktopDb() at app startup.'
    );
  }
  return desktopDatabase;
}

/**
 * Initialize database at app startup
 * Call this before rendering any components that need database access
 */
export async function initializeDesktopDb(): Promise<void> {
  if (desktopDatabase !== null) {
    console.log('[Desktop DB] Already initialized');
    return;
  }

  console.log('[Desktop DB] Initializing PGlite...');
  await getDesktopDb();
  console.log('[Desktop DB] Ready');
}

/**
 * Export db for compatibility with existing imports
 * This is a Proxy that throws helpful errors if used before initialization
 */
const createDbHandler = (): ProxyHandler<Kysely<DesktopSchema>> => ({
  get(_target, property, receiver): unknown {
    const instance = desktopDatabase;
    if (instance === null) {
      throw new Error(
        `Desktop database accessed before initialization (property: ${String(property)}). ` +
          'Call initializeDesktopDb() at app startup, or use getDesktopDb() for async access.'
      );
    }
    const value: unknown = Reflect.get(instance, property, receiver);
    if (typeof value === 'function') {
      return (...args: unknown[]): unknown => value.apply(instance, args);
    }
    return value;
  },
});

// Proxy target is never used directly; Proxy requires a concrete object.
// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
const proxyTarget = Object.create(null) as unknown as Kysely<DesktopSchema>;
// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion, typescript-eslint/no-unnecessary-type-assertion
export const db = new Proxy(proxyTarget, createDbHandler()) as Kysely<DesktopSchema>;

/**
 * Expose Kysely's tagged template helper for API parity with the cloud runtime.
 */

const createSqlUnavailableError = (property?: string | symbol): Error => {
  if (property) {
    return new Error(
      `postgres.js client "${String(property)}" is unavailable in desktop mode. Use the Kysely instance instead.`
    );
  }
  return new Error('postgres.js client is unavailable in desktop mode');
};

/**
 * postgres.js client is not available in desktop mode; provide a lazy thrower.
 */
const unsupportedSql = (() => {
  const thrower = () => {
    throw createSqlUnavailableError();
  };
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return new Proxy(thrower, {
    get: () => thrower,
    apply: () => {
      thrower();
    },
  }) as unknown as Sql;
})();

export const sql = new Proxy(unsupportedSql, {
  apply() {
    throw createSqlUnavailableError();
  },
  get(_target, property) {
    throw createSqlUnavailableError(property);
  },
});
/* eslint-enable @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/no-unsafe-assignment */

if (isDesktopBuild()) {
  try {
    await getDesktopDb();
  } catch (error) {
    console.error('[Desktop DB] Warmup failed', error);
  }
}

// Re-export Kysely helpers + schema types for compatibility with cloud runtime imports.
export { sql as kyselySql } from 'kysely';
export type { DB } from './schema';
